import { useState } from "react";
import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { MdTimeline } from "react-icons/md";
import ExpandableDataTable, { DetailGrid, DetailItem, DetailSection } from "components/admin/ExpandableDataTable";
import SettlementStatement from "components/admin/SettlementStatement";
import SettlementBreakdown from "components/admin/SettlementBreakdown";
import StatusBadge from "components/admin/StatusBadge";
import usePaginatedResource from "hooks/usePaginatedResource";
import { apiGet } from "lib/apiClient";
import { formatKes } from "lib/format";

const columnHelper = createColumnHelper();

export const SETTLEMENT_STATUS_TONE = {
  CALCULATED: "gray",
  PENDING_APPROVAL: "amber",
  APPROVED: "blue",
  PROCESSING: "blue",
  PAID: "green",
  FAILED: "red",
  REVERSED: "red",
};

export const SETTLEMENT_STATUS_LABEL = {
  CALCULATED: "Calculated",
  PENDING_APPROVAL: "Pending approval",
  APPROVED: "Approved",
  PROCESSING: "Processing",
  PAID: "Paid",
  FAILED: "Failed",
  REVERSED: "Reversed",
};

export function settlementPeriod(s) {
  return `${new Date(s.periodStart).toLocaleDateString()} – ${new Date(s.periodEnd).toLocaleDateString()}`;
}

export function SettlementStatusBadge({ status }) {
  return <StatusBadge label={SETTLEMENT_STATUS_LABEL[status] || status} tone={SETTLEMENT_STATUS_TONE[status] || "gray"} />;
}

export function settlementColumns(helper) {
  return [
    helper.accessor("periodStart", {
      header: "Period",
      cell: (info) => <p className="text-sm font-semibold text-navy-700 dark:text-white">{settlementPeriod(info.row.original)}</p>,
      meta: { wrap: true },
    }),
    helper.accessor("netRevenue", {
      header: "Net revenue",
      cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{formatKes(info.getValue(), info.row.original.currency)}</p>,
      meta: { hideBelow: "md" },
    }),
    helper.accessor("clientShareAmount", {
      header: "Your share",
      cell: (info) => <p className="text-sm font-semibold text-navy-700 dark:text-white">{formatKes(info.getValue(), info.row.original.currency)}</p>,
    }),
    helper.accessor("status", {
      header: "Status",
      cell: (info) => <SettlementStatusBadge status={info.getValue()} />,
    }),
  ];
}

export const settlementExportColumns = [
  { header: "Settlement ID", accessorKey: "id" },
  { header: "Period start", accessorKey: "periodStart" },
  { header: "Period end", accessorKey: "periodEnd" },
  { header: "Currency", accessorKey: "currency" },
  { header: "Gross revenue", accessorKey: "grossRevenue" },
  { header: "Deductions", accessorKey: "deductions" },
  { header: "Net revenue", accessorKey: "netRevenue" },
  { header: "Share %", accessorKey: "clientSharePercentage" },
  { header: "Your share", accessorKey: "clientShareAmount" },
  { header: "Status", accessorKey: "status" },
  { header: "Failure reason", accessorKey: "failureReason" },
  { header: "Reversal reason", accessorKey: "reversalReason" },
];

export function renderSettlementDetail(s) {
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
      <DetailSection title="How your share was calculated" className="lg:col-span-3">
        <SettlementStatement settlement={s} />
        <p className="mb-2 mt-4 text-sm font-semibold text-navy-700 dark:text-white">Line items by source</p>
        <SettlementBreakdown url={`/portal/settlements/${s.id}/breakdown`} />
      </DetailSection>
      <DetailSection title="Payout record" className="lg:col-span-2">
        <DetailGrid columns={2}>
          <DetailItem label="Status">
            <SettlementStatusBadge status={s.status} />
          </DetailItem>
          <DetailItem label="Period">{settlementPeriod(s)}</DetailItem>
          <DetailItem label="Currency">{s.currency}</DetailItem>
          {s.failureReason && (
            <DetailItem label="Failure reason" span={2}>
              <span className="text-red-500">{s.failureReason}</span>
            </DetailItem>
          )}
          {s.reversalReason && (
            <DetailItem label="Reversal reason" span={2}>
              <span className="text-red-500">{s.reversalReason}</span>
            </DetailItem>
          )}
          <DetailItem label="Settlement ID" mono span={2}>
            {s.id}
          </DetailItem>
          <DetailItem label="Ledger transaction" mono span={2}>
            {s.ledgerTransactionId}
          </DetailItem>
        </DetailGrid>
      </DetailSection>
    </div>
  );
}

export function MoneyTimelineLink({ to = "/portal/money-timeline?type=CLIENT_SETTLEMENT", label = "Money timeline" }) {
  return (
    <Link
      to={to}
      className="flex h-9 items-center gap-1.5 whitespace-nowrap rounded-sm border border-gray-200 px-3 text-xs font-semibold text-navy-700 transition hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-navy-700"
    >
      <MdTimeline className="text-sm" />
      {label}
    </Link>
  );
}

export function SettlementDetailModal({ settlement, loading, error, onClose }) {
  if (!settlement && !loading && !error) return null;
  return (
    <div
      role="region"
      aria-label="Settlement detail"
      className="mt-4 w-full max-w-lg rounded-xl border-2 border-brand-500/30 bg-white p-6 shadow-lg dark:bg-navy-800"
    >
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : error ? (
          <p className="text-sm font-medium text-red-500">{error}</p>
        ) : (
          <>
            <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Settlement detail</h4>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{settlementPeriod(settlement)}</p>
            <SettlementStatement settlement={settlement} className="mt-4" />
            <div className="mt-4 space-y-2 text-sm">
              <p>
                <span className="font-semibold text-navy-700 dark:text-white">Status:</span> <SettlementStatusBadge status={settlement.status} />
              </p>
              {settlement.failureReason && (
                <p>
                  <span className="font-semibold text-navy-700 dark:text-white">Failure reason:</span> {settlement.failureReason}
                </p>
              )}
              {settlement.reversalReason && (
                <p>
                  <span className="font-semibold text-navy-700 dark:text-white">Reversal reason:</span> {settlement.reversalReason}
                </p>
              )}
              <p className="break-all">
                <span className="font-semibold text-navy-700 dark:text-white">Settlement ID:</span> {settlement.id}
              </p>
            </div>
          </>
        )}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm border border-gray-200 px-4 py-2 text-sm font-semibold text-navy-700 dark:border-white/10 dark:text-white"
          >
            Close
          </button>
        </div>
    </div>
  );
}

export function useSettlementDetail() {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  async function openDetail(id) {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    try {
      const result = await apiGet(`/portal/settlements/${id}`);
      setDetail(result.settlement);
    } catch (err) {
      setDetailError(err.message || "Failed to load settlement.");
    } finally {
      setDetailLoading(false);
    }
  }

  function closeDetail() {
    setDetailOpen(false);
    setDetail(null);
    setDetailError(null);
  }

  return { detail, detailLoading, detailError, detailOpen, openDetail, closeDetail };
}

export function viewButtonClass() {
  return "rounded-lg bg-lightPrimary px-3 py-2 text-xs font-semibold text-navy-700 dark:bg-navy-700 dark:text-white";
}

export default function SettlementsPage() {
  const { items, total, loading, error, page, setPage, pageSize, setPageSize, refetch, exportAll } = usePaginatedResource("/portal/settlements", {
    pageSize: 20,
    itemsKey: "settlements",
    pollMs: 15000,
  });

  const { detail, detailLoading, detailError, detailOpen, openDetail, closeDetail } = useSettlementDetail();
  const columns = settlementColumns(columnHelper);

  return (
    <div>
      <ExpandableDataTable
        title="Settlements"
        subtitle="Click a period to see gross, deductions, net and exactly how your share was worked out."
        columns={columns}
        exportColumns={settlementExportColumns}
        data={items}
        loading={loading}
        error={error}
        onRetry={refetch}
        emptyMessage="No settlements have been calculated for your organization yet."
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
      onPageSizeChange={setPageSize}
        exportFilename="settlements"
        onExportAll={exportAll}
        rowLabel={(s) => `settlement ${settlementPeriod(s)}`}
        headerActions={<MoneyTimelineLink />}
        renderDetail={renderSettlementDetail}
        renderActions={(s) => (
          <>
            <button type="button" onClick={() => openDetail(s.id)} className={viewButtonClass()}>
              View full record
            </button>
            <Link to="/portal/money-timeline?type=CLIENT_SETTLEMENT" className={viewButtonClass()}>
              See it on the money timeline
            </Link>
          </>
        )}
      />

      {detailOpen && <SettlementDetailModal settlement={detail} loading={detailLoading} error={detailError} onClose={closeDetail} />}
    </div>
  );
}
